import { useState, useEffect, useRef, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLocationScope } from "@/context/LocationContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import StructureBlock from "@/components/StructureBlock";
import { Loader2, Save, Plus, Trash2, PencilRuler, Truck, Ban, MapPin } from "lucide-react";
import { toast } from "sonner";

const CELL = 58;
const STATUS_BG = {
  Available: "bg-emerald-900/60 border-emerald-500/60 text-emerald-200",
  Occupied: "bg-red-900/60 border-red-500/60 text-red-200",
  Reserved: "bg-amber-900/60 border-amber-500/60 text-amber-200",
  OutOfService: "bg-slate-800 border-slate-600 text-slate-500",
};
const SIZE_OPTS = ["Small", "Medium", { value: "Pole", label: "53 ft" }, "Car Hauler", "Other"];
const STATUS_OPTS = ["Available", "Occupied", "Reserved", "OutOfService"];
const ORIENT_OPTS = ["horizontal", "vertical", "diagonal"];
const STRUCT_TYPES = ["structure", "reservedBlock", "outOfService"];

export default function LayoutEditor() {
  const { selectedLot, isAll, currentLot, loaded } = useLocationScope();
  const lotId = isAll ? null : selectedLot;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cols, setCols] = useState(12);
  const [rows, setRows] = useState(12);
  const [spots, setSpots] = useState([]);
  const [structures, setStructures] = useState([]);
  const [sel, setSel] = useState(null); // {type:'spot'|'structure', id}
  const [drag, setDrag] = useState(null);
  const gridRef = useRef(null);

  useEffect(() => {
    if (!lotId) return;
    setLoading(true);
    api.get(`/lots/${lotId}/layout`).then(({ data }) => {
      setCols(data.lot.mapCols || 12);
      setRows(data.lot.mapRows || 12);
      setSpots((data.spots || []).map((s) => ({ ...s })));
      setStructures((data.structures || []).map((s) => ({ ...s })));
      setSel(null);
      setLoading(false);
    });
  }, [lotId]);

  const cellFromPointer = useCallback(
    (e) => {
      const r = gridRef.current.getBoundingClientRect();
      const pad = 16;
      const x = Math.floor((e.clientX - r.left - pad) / (CELL + 6));
      const y = Math.floor((e.clientY - r.top - pad) / (CELL + 6));
      return {
        x: Math.max(0, Math.min(cols - 1, x)),
        y: Math.max(0, Math.min(rows - 1, y)),
      };
    },
    [cols, rows]
  );

  const onPointerMove = (e) => {
    if (!drag) return;
    const { x, y } = cellFromPointer(e);
    if (drag.type === "spot") {
      setSpots((arr) => arr.map((s) => (s.id === drag.id ? { ...s, posX: x, posY: y } : s)));
    } else {
      setStructures((arr) => arr.map((s) => (s.id === drag.id ? { ...s, posX: x, posY: y } : s)));
    }
  };

  const startDrag = (type, id) => (e) => {
    e.stopPropagation();
    setSel({ type, id });
    setDrag({ type, id });
  };

  const updateSel = (patch) => {
    if (!sel) return;
    if (sel.type === "spot") setSpots((a) => a.map((s) => (s.id === sel.id ? { ...s, ...patch } : s)));
    else setStructures((a) => a.map((s) => (s.id === sel.id ? { ...s, ...patch } : s)));
  };

  const deleteSel = () => {
    if (!sel) return;
    if (sel.type === "spot") setSpots((a) => a.filter((s) => s.id !== sel.id));
    else setStructures((a) => a.filter((s) => s.id !== sel.id));
    setSel(null);
  };

  const firstFreeCell = () => {
    const taken = new Set([
      ...spots.filter((s) => s.posX != null).map((s) => `${s.posX},${s.posY}`),
    ]);
    for (let y = 0; y < rows; y++)
      for (let x = 0; x < cols; x++) if (!taken.has(`${x},${y}`)) return { x, y };
    return { x: 0, y: 0 };
  };

  const addSpot = () => {
    const { x, y } = firstFreeCell();
    const nums = spots.map((s) => parseInt(s.spotNumber, 10)).filter((n) => !isNaN(n));
    const next = (nums.length ? Math.max(...nums) : 0) + 1;
    const id = `new-${crypto.randomUUID ? crypto.randomUUID() : Date.now()}`;
    const spot = {
      id,
      spotNumber: String(next),
      sizeClass: "Medium",
      tenancyType: "Daily",
      status: "Available",
      zone: "General",
      zoneLabel: null,
      orientation: "horizontal",
      posX: x,
      posY: y,
    };
    setSpots((a) => [...a, spot]);
    setSel({ type: "spot", id });
  };

  const addStructure = () => {
    const id = `new-${crypto.randomUUID ? crypto.randomUUID() : Date.now()}`;
    const st = {
      id,
      label: "New Zone",
      type: "structure",
      posX: 0,
      posY: 0,
      width: 3,
      height: 2,
      color: "#334155",
    };
    setStructures((a) => [...a, st]);
    setSel({ type: "structure", id });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        mapCols: Number(cols),
        mapRows: Number(rows),
        spots: spots.map((s) => ({
          id: String(s.id).startsWith("new-") ? null : s.id,
          spotNumber: s.spotNumber,
          sizeClass: s.sizeClass,
          tenancyType: s.tenancyType,
          status: s.status,
          zone: s.zone || "General",
          zoneLabel: s.zoneLabel || null,
          posX: s.posX,
          posY: s.posY,
          orientation: s.orientation || "horizontal",
        })),
        structures: structures.map((s) => ({
          id: String(s.id).startsWith("new-") ? null : s.id,
          label: s.label,
          type: s.type,
          posX: s.posX,
          posY: s.posY,
          width: Number(s.width),
          height: Number(s.height),
          color: s.color || null,
        })),
      };
      const { data } = await api.put(`/lots/${lotId}/layout`, payload);
      setSpots((data.spots || []).map((s) => ({ ...s })));
      setStructures((data.structures || []).map((s) => ({ ...s })));
      setSel(null);
      toast.success("Layout saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loaded && !lotId) {
    return (
      <div className="flex h-[70vh] flex-col items-center justify-center gap-3 text-slate-400">
        <MapPin className="h-10 w-10 text-[#A0148C]" />
        <p data-testid="layout-pick-lot" className="text-lg">Pick a specific location from the switcher above to edit its map.</p>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="flex h-[70vh] items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-[#A0148C]" />
      </div>
    );
  }

  const selItem =
    sel && (sel.type === "spot" ? spots.find((s) => s.id === sel.id) : structures.find((s) => s.id === sel.id));

  return (
    <div className="px-4 py-6 md:px-8">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-800">
            <PencilRuler className="h-6 w-6 text-[#A0148C]" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-black tracking-tight text-slate-50">Layout Editor</h1>
            <p className="text-slate-400">{currentLot?.name} — drag spots &amp; structures to match the yard.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="layout-add-spot" onClick={addSpot} variant="secondary" className="gap-2 bg-slate-800 hover:bg-slate-700">
            <Plus className="h-4 w-4" /> Spot
          </Button>
          <Button data-testid="layout-add-structure" onClick={addStructure} variant="secondary" className="gap-2 bg-slate-800 hover:bg-slate-700">
            <Plus className="h-4 w-4" /> Structure
          </Button>
          <Button data-testid="layout-save" onClick={save} disabled={saving} className="gap-2 bg-[#A0148C] font-bold hover:bg-[#80106F]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Layout
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <Label className="text-xs text-slate-400">Grid Cols</Label>
          <Input data-testid="layout-cols" type="number" value={cols} onChange={(e) => setCols(e.target.value)} className="h-10 w-24 border-slate-700 bg-slate-950" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-400">Grid Rows</Label>
          <Input data-testid="layout-rows" type="number" value={rows} onChange={(e) => setRows(e.target.value)} className="h-10 w-24 border-slate-700 bg-slate-950" />
        </div>
        <p className="text-sm text-slate-500">{spots.length} spots · {structures.length} structures</p>
      </div>

      <div className="mt-5 flex flex-col gap-5 lg:flex-row">
        {/* Editable grid */}
        <div className="overflow-auto rounded-xl border border-slate-800 bg-slate-950/60 p-2">
          <div
            ref={gridRef}
            onPointerMove={onPointerMove}
            onPointerUp={() => setDrag(null)}
            onPointerLeave={() => setDrag(null)}
            className="relative inline-grid gap-1.5 p-4"
            style={{
              gridTemplateColumns: `repeat(${cols}, ${CELL}px)`,
              gridAutoRows: `${CELL}px`,
              touchAction: "none",
            }}
          >
            {/* grid background cells */}
            {Array.from({ length: cols * rows }).map((_, i) => (
              <div
                key={`bg-${i}`}
                style={{ gridColumnStart: (i % cols) + 1, gridRowStart: Math.floor(i / cols) + 1 }}
                className="rounded-md border border-dashed border-slate-800/70"
              />
            ))}

            {structures.map((st) => (
              <div
                key={st.id}
                data-testid={`layout-structure-${st.id}`}
                onPointerDown={startDrag("structure", st.id)}
                style={{
                  gridColumn: `${st.posX + 1} / span ${st.width}`,
                  gridRow: `${st.posY + 1} / span ${st.height}`,
                }}
                className="cursor-move"
              >
                <StructureBlock
                  structure={{ ...st, posX: 0, posY: 0 }}
                  selected={sel?.type === "structure" && sel.id === st.id}
                />
              </div>
            ))}

            {spots
              .filter((s) => s.posX != null && s.posY != null)
              .map((s) => (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`layout-spot-${s.id}`}
                  onPointerDown={startDrag("spot", s.id)}
                  style={{ gridColumnStart: s.posX + 1, gridRowStart: s.posY + 1 }}
                  className={`relative flex cursor-move flex-col items-center justify-center rounded-md border-2 ${STATUS_BG[s.status]} ${
                    sel?.type === "spot" && sel.id === s.id ? "ring-2 ring-white" : ""
                  }`}
                >
                  <span className="absolute left-1 top-0.5 font-data text-[9px] font-bold text-slate-100">{s.spotNumber}</span>
                  {s.status === "OutOfService" ? <Ban className="h-4 w-4" /> : <Truck className="h-4 w-4" />}
                </button>
              ))}
          </div>
        </div>

        {/* Properties panel */}
        <Card className="h-fit w-full border-slate-800 bg-slate-900 p-5 lg:w-80">
          {!selItem ? (
            <p className="text-sm text-slate-400">Tap a spot or structure to edit it, or drag to reposition. Use the buttons above to add new ones.</p>
          ) : sel.type === "spot" ? (
            <div className="space-y-3" data-testid="layout-panel-spot">
              <h3 className="font-heading text-lg font-bold text-slate-100">Spot {selItem.spotNumber}</h3>
              <PanelField label="Spot number" testid="panel-spot-number" value={selItem.spotNumber} onChange={(v) => updateSel({ spotNumber: v })} />
              <PanelSelect label="Size" testid="panel-spot-size" value={selItem.sizeClass} opts={SIZE_OPTS} onChange={(v) => updateSel({ sizeClass: v })} />
              <PanelSelect label="Tenancy" testid="panel-spot-tenancy" value={selItem.tenancyType} opts={["Daily", "Monthly"]} onChange={(v) => updateSel({ tenancyType: v })} />
              <PanelSelect label="Status" testid="panel-spot-status" value={selItem.status} opts={STATUS_OPTS} onChange={(v) => updateSel({ status: v })} />
              <PanelSelect label="Orientation" testid="panel-spot-orientation" value={selItem.orientation || "horizontal"} opts={ORIENT_OPTS} onChange={(v) => updateSel({ orientation: v })} />
              <div className="grid grid-cols-2 gap-2">
                <PanelField label="X" testid="panel-spot-x" type="number" value={selItem.posX} onChange={(v) => updateSel({ posX: Number(v) })} />
                <PanelField label="Y" testid="panel-spot-y" type="number" value={selItem.posY} onChange={(v) => updateSel({ posY: Number(v) })} />
              </div>
              <Button data-testid="layout-delete" onClick={deleteSel} variant="secondary" className="w-full gap-2 bg-red-900/60 text-red-200 hover:bg-red-900">
                <Trash2 className="h-4 w-4" /> Delete spot
              </Button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="layout-panel-structure">
              <h3 className="font-heading text-lg font-bold text-slate-100">Structure</h3>
              <PanelField label="Label" testid="panel-struct-label" value={selItem.label} onChange={(v) => updateSel({ label: v })} />
              <PanelSelect label="Type" testid="panel-struct-type" value={selItem.type} opts={STRUCT_TYPES} onChange={(v) => updateSel({ type: v })} />
              <div className="grid grid-cols-2 gap-2">
                <PanelField label="Width" testid="panel-struct-w" type="number" value={selItem.width} onChange={(v) => updateSel({ width: Number(v) })} />
                <PanelField label="Height" testid="panel-struct-h" type="number" value={selItem.height} onChange={(v) => updateSel({ height: Number(v) })} />
                <PanelField label="X" testid="panel-struct-x" type="number" value={selItem.posX} onChange={(v) => updateSel({ posX: Number(v) })} />
                <PanelField label="Y" testid="panel-struct-y" type="number" value={selItem.posY} onChange={(v) => updateSel({ posY: Number(v) })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Color</Label>
                <input data-testid="panel-struct-color" type="color" value={selItem.color || "#334155"} onChange={(e) => updateSel({ color: e.target.value })} className="h-10 w-full rounded-md border border-slate-700 bg-slate-950" />
              </div>
              <Button data-testid="layout-delete" onClick={deleteSel} variant="secondary" className="w-full gap-2 bg-red-900/60 text-red-200 hover:bg-red-900">
                <Trash2 className="h-4 w-4" /> Delete structure
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function PanelField({ label, value, onChange, testid, type = "text" }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-400">{label}</Label>
      <Input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-10 border-slate-700 bg-slate-950" />
    </div>
  );
}

function PanelSelect({ label, value, opts, onChange, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-400">{label}</Label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-2 text-slate-100">
        {opts.map((o) => {
          const val = typeof o === "string" ? o : o.value;
          const label = typeof o === "string" ? o : o.label;
          return <option key={val} value={val}>{label}</option>;
        })}
      </select>
    </div>
  );
}
